import { MembershipRoles } from '../data-models/membership-roles.model';

/**
 * Helper function to check if user has a specific role
 */
export function hasRole(roleFlags: number, role: MembershipRoles): boolean {
  return (roleFlags & role) === role;
}

/**
 * Helper function to get role display name
 */
export function getRoleName(role: MembershipRoles): string {
  switch (role) {
    case MembershipRoles.Pilot:
      return 'Pilot';
    case MembershipRoles.PilotInCharge:
      return 'PIC';
    case MembershipRoles.Owner:
      return 'Owner';
    case MembershipRoles.ChiefSupervisor:
      return 'Chief Supervisor';
    default:
      return 'Unknown';
  }
}

/**
 * Helper function to get role badge class
 */
export function getRoleBadgeClass(role: MembershipRoles): string {
  switch (role) {
    case MembershipRoles.Owner:
      return 'badge bg-primary';
    case MembershipRoles.ChiefSupervisor:
      return 'badge bg-info';
    case MembershipRoles.PilotInCharge:
      return 'badge bg-success';
    case MembershipRoles.Pilot:
      return 'badge bg-secondary';
    default:
      return 'badge bg-secondary';
  }
}

/**
 * Helper function to get all roles for a member
 */
export function getMemberRoles(roleFlags: number): MembershipRoles[] {
  const roles: MembershipRoles[] = [];
  if (hasRole(roleFlags, MembershipRoles.Owner)) roles.push(MembershipRoles.Owner);
  if (hasRole(roleFlags, MembershipRoles.ChiefSupervisor)) roles.push(MembershipRoles.ChiefSupervisor);
  if (hasRole(roleFlags, MembershipRoles.PilotInCharge)) roles.push(MembershipRoles.PilotInCharge);
  if (hasRole(roleFlags, MembershipRoles.Pilot)) roles.push(MembershipRoles.Pilot);
  return roles;
}

/**
 * Helper functions for role management
 */
export class RoleHelper {
  /**
   * Check if role has a specific flag
   */
  static hasRole(role: number, flag: MembershipRoles): boolean {
    return hasRole(role, flag);
  }

  /**
   * Add a role flag
   */
  static addRole(role: number, flag: MembershipRoles): number {
    return role | flag;
  }

  /**
   * Remove a role flag
   */
  static removeRole(role: number, flag: MembershipRoles): number {
    return role & ~flag;
  }

  /**
   * Get role names array
   */
  static getRoleNames(role: number): string[] {
    const roles: string[] = [];
    if (this.hasRole(role, MembershipRoles.Owner)) roles.push('Owner');
    if (this.hasRole(role, MembershipRoles.ChiefSupervisor)) roles.push('Chief Supervisor');
    if (this.hasRole(role, MembershipRoles.PilotInCharge)) roles.push('Pilot In Command');
    if (this.hasRole(role, MembershipRoles.Pilot)) roles.push('Pilot');
    return roles;
  }

  /**
   * Get role badge class
   */
  static getRoleBadgeClass(roleName: string): string {
    switch (roleName) {
      case 'Owner':
        return 'badge bg-primary';
      case 'Chief Supervisor':
        return 'badge bg-success';
      case 'Pilot In Command':
        return 'badge bg-info';
      case 'Pilot':
        return 'badge bg-secondary';
      default:
        return 'badge bg-secondary';
    }
  }

  /**
   * Calculate days remaining until expiration
   */
  static getDaysRemaining(expiresAt: Date | string): number {
    const expires = new Date(expiresAt);
    const now = new Date();
    const diffMs = expires.getTime() - now.getTime();
    return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  }

  /**
   * Check if invitation is expired
   */
  static isExpired(expiresAt: Date | string): boolean {
    return new Date(expiresAt) < new Date();
  }

  /**
   * Check if invitation can be resent (24 hour rate limit)
   */
  static canResend(lastResentAt: Date | string | null | undefined, resendCount: number): boolean {
    // Max 5 resends
    if (resendCount >= 5) return false;

    // No previous resend, can resend
    if (!lastResentAt) return true;

    // Check 24 hour rate limit
    const lastResent = new Date(lastResentAt);
    const now = new Date();
    const hoursSinceResend = (now.getTime() - lastResent.getTime()) / (1000 * 60 * 60);
    return hoursSinceResend >= 24;
  }
}
